import {
  Button,
  ButtonGroup,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  type MaterialSymbol,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useGridForm,
} from "@qw/ui";
import type { ComponentProps, FC } from "react";

export interface BulkActionsMenuItem {
  icon: MaterialSymbol;
  label: string;
  onClick: () => void;
  iconProps?: ComponentProps<typeof Icon>;
}

export interface BulkActionsActions {
  items: BulkActionsMenuItem[];
  moreItems?: BulkActionsMenuItem[];
}

export interface BulkActionsProps {
  actions: BulkActionsActions;
  isLoading?: boolean;
}

export const BulkActions: FC<BulkActionsProps> = ({ actions, isLoading }) => {
  const { clearSelection, hasSelection, selectedRowIds } = useGridForm();

  if (!hasSelection) {
    return null;
  }

  return (
    <ButtonGroup className="gap-0 relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={clearSelection}
        disabled={isLoading}
        className="font-normal -ml-2 px-2 h-6 gap-1.5"
      >
        {selectedRowIds.length} selected <Icon icon="close" />
      </Button>

      <span className="flex-1" />

      {isLoading && <Spinner className="text-sm mx-2" />}

      {actions.items.map((action) => (
        <Tooltip key={action.icon}>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" display="icon" onClick={action.onClick} disabled={isLoading}>
              <Icon icon={action.icon} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{action.label}</TooltipContent>
        </Tooltip>
      ))}

      {!!actions.moreItems?.length && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" display="icon" disabled={isLoading}>
              <Icon icon="more_vert" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {actions.moreItems.map((action) => (
              <DropdownMenuItem key={action.icon} onClick={action.onClick} disabled={isLoading}>
                <Icon icon={action.icon} {...action.iconProps} />
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </ButtonGroup>
  );
};
